import React, { useEffect, useMemo, useRef } from 'react';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from '@mui/styles';
import { IconButton, Tooltip } from '@mui/material';
import { useMessage } from 'modules/intl';
import { useCollectorSettingLazyQuery } from 'graphql/types';
import { useCollectorSyncStatusContext } from './contexts/CollectorSyncStatusContextProvider';
import { SyncStatus } from './utils';

const useStyles = makeStyles((theme) => ({
  syncInformation: {
    padding: theme.spacing(0.2, 0.2),
    ...theme.typography.caption,
    color: theme.palette.grey[900],
    backgroundColor: theme.palette.grey[500],
    marginRight: theme.spacing(1.5),
    fontSize: 22,
    top: theme.spacing(-1.2),
    float: 'right',
    position: 'relative',
    bottom: theme.spacing(1.2),
    borderRadius: '50%'
  },
  syncFailed: {
    padding: theme.spacing(0.2, 0.2),
    ...theme.typography.caption,
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
    fontSize: 22,
    borderRadius: '50%'
  },
  syncFailedButton: {
    padding: '0px',
    marginRight: theme.spacing(1.5),
    top: theme.spacing(-1.2),
    float: 'right',
    position: 'relative',
    bottom: theme.spacing(1.2),
    borderRadius: '50%'
  }
}));
type CollectorStatusProps = {
  syncStatus: string;
  installId: string;
  userInstallId?: string;
  userSyncStatus?: string;
  onClick: () => void;
};
export const CollectorStatus: React.FC<CollectorStatusProps> = ({
  syncStatus,
  installId,
  userInstallId,
  userSyncStatus,
  onClick: openSettingsDialog
}: CollectorStatusProps) => {
  const {
    collectorsSyncStatus,
    setCollectorSyncStatus
  } = useCollectorSyncStatusContext();
  const tooltipMessage =
    useMessage('collectorSetting.details.iconErrorTooltip') || '';
  useEffect(() => {
    if ((syncStatus || '').length > 0 && installId) {
      setCollectorSyncStatus(installId, syncStatus);
    }
    if ((userSyncStatus || '').length > 0 && userInstallId) {
      setCollectorSyncStatus(userInstallId, userSyncStatus);
    }
  }, [
    installId,
    setCollectorSyncStatus,
    syncStatus,
    userInstallId,
    userSyncStatus
  ]);

  const interval = useRef<NodeJS.Timeout | null>(null);
  const userInterval = useRef<NodeJS.Timeout | null>(null);
  const [getSettings, { data, loading }] = useCollectorSettingLazyQuery();
  const [
    getUserSettings,
    { data: userData, loading: userLoading }
  ] = useCollectorSettingLazyQuery();

  useEffect(() => {
    if (collectorsSyncStatus[installId] === SyncStatus.inProgress) {
      interval.current = setInterval(() => {
        getSettings({
          variables: { input: { installId: installId! } }
        });
      }, 3000);
    }
    if (
      userInstallId &&
      collectorsSyncStatus[userInstallId] === SyncStatus.inProgress
    ) {
      userInterval.current = setInterval(() => {
        getUserSettings({
          variables: { input: { installId: userInstallId! } }
        });
      }, 3000);
    }

    return () => {
      if (interval.current) clearInterval(interval.current);
      if (userInterval.current) clearInterval(userInterval.current);
    };
  }, [
    collectorsSyncStatus,
    getSettings,
    getUserSettings,
    installId,
    userInstallId
  ]);

  useEffect(() => {
    if (!loading && data?.collectorSetting?.item?.settings) {
      const settings = data.collectorSetting.item.settings;
      const syncStatus = settings?.find((s) => s?.key === 'syncStatus')?.value;
      if (syncStatus !== SyncStatus.inProgress) {
        setCollectorSyncStatus(installId, syncStatus);
        if (interval.current) clearInterval(interval.current);
      }
    }
    if (!userLoading && userData?.collectorSetting?.item?.settings) {
      const settings = userData.collectorSetting.item.settings;
      const syncStatus = settings?.find((s) => s?.key === 'syncStatus')?.value;
      if (syncStatus !== SyncStatus.inProgress && userInstallId) {
        setCollectorSyncStatus(userInstallId, syncStatus);
        if (userInterval.current) clearInterval(userInterval.current);
      }
    }
  }, [
    data?.collectorSetting?.item?.settings,
    setCollectorSyncStatus,
    loading,
    installId,
    userLoading,
    userData?.collectorSetting?.item?.settings,
    userInstallId
  ]);

  const classes = useStyles();
  const syncIconComponent = useMemo(() => {
    if (
      collectorsSyncStatus[installId] === SyncStatus.inProgress ||
      collectorsSyncStatus[userInstallId as string] === SyncStatus.inProgress
    ) {
      return (
        <SyncIcon className={classes.syncInformation} data-testid="SyncIcon" />
      );
    }
    return <></>;
  }, [classes.syncInformation, collectorsSyncStatus, installId, userInstallId]);
  return (
    <>
      {syncIconComponent}
      {(collectorsSyncStatus[installId] === SyncStatus.failed ||
        collectorsSyncStatus[installId] === SyncStatus.partiallyCompleted ||
        collectorsSyncStatus[userInstallId as string] === SyncStatus.failed ||
        collectorsSyncStatus[userInstallId as string] ===
          SyncStatus.partiallyCompleted) && (
        <Tooltip title={tooltipMessage} data-testid="collector-status-tooltip">
          <IconButton
            onClick={openSettingsDialog}
            className={classes.syncFailedButton}
          >
            <ErrorIcon className={classes.syncFailed} data-testid="ErrorIcon" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};
export default CollectorStatus;
